import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Sidebar from "../components/Sidebar"; // ✅ Sidebar Import
import { FaSearch, FaEye, FaTrash } from "react-icons/fa";
import { showSuccess, showError } from "../utils/toast";

const AddFamilyMember = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false); // ✅ Sidebar state
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const [activeTab, setActiveTab] = useState("member");
  const [groupId, setGroupId] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [searchCustomer, setSearchCustomer] = useState({ name: "", policyNumber: "" });
  const [memberQuery, setMemberQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showPolicyForm, setShowPolicyForm] = useState(false);
  const [isPolicyFetched, setIsPolicyFetched] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [customerShowSuggestions, setCustomerShowSuggestions] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");

  const [form, setForm] = useState({
    policyNumber: "",
    primaryHolder: "",
    memberName: "",
    relation: "",
    dob: "",
    age: "",
  });

  const [policyForm, setPolicyForm] = useState({
    policyNumber: "",
    policyHolderName: "",
    contact: "",
    email: "",
    company: "",
    category: "",
    type: "",
    premium: "",
    paymentMode: "",
    startDate: "",
    maturityDate: "",
    branch: "",
  });

  const [familyList, setFamilyList] = useState([]);

  // =========================
  // ✅ VALIDATION UTILITIES
  // =========================
  const namePattern = /^[a-zA-Z\s'.-]{2,60}$/;
  const phonePattern = /^[6-9]\d{9}$/;
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const policyNoPattern = /^[A-Za-z0-9\-_/]{3,40}$/;
  const moneyPattern = /^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/;

  const [errors, setErrors] = useState({
    search: {},
    member: {},
    policy: {},
  });

  const setFieldError = (section, field, message) => {
    setErrors(prev => ({ ...prev, [section]: { ...prev[section], [field]: message } }));
  };
  const clearFieldError = (section, field) => {
    setErrors(prev => {
      const copy = { ...prev[section] };
      delete copy[field];
      return { ...prev, [section]: copy };
    });
  };

  const validateSearchCustomer = () => {
    let ok = true;
    if (!searchCustomer.name.trim() || !namePattern.test(searchCustomer.name.trim())) {
      setFieldError("search", "name", "Enter a valid customer name (min 2 letters).");
      ok = false;
    } else clearFieldError("search", "name");

    if (!searchCustomer.policyNumber.trim() || !policyNoPattern.test(searchCustomer.policyNumber.trim())) {
      setFieldError("search", "policyNumber", "Enter a valid policy number (3–40 chars, A-Z 0-9 - _ /).");
      ok = false;
    } else clearFieldError("search", "policyNumber");

    return ok;
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age.toString();
  };

  const validateMemberForm = () => {
    let ok = true;

    if (!form.policyNumber) {
      setFieldError("member", "policyNumber", "Policy Number is required.");
      ok = false;
    } else clearFieldError("member", "policyNumber");

    if (!form.primaryHolder) {
      setFieldError("member", "primaryHolder", "Primary Holder is required.");
      ok = false;
    } else clearFieldError("member", "primaryHolder");

    if (!form.memberName.trim() || !namePattern.test(form.memberName.trim())) {
      setFieldError("member", "memberName", "Enter a valid member name (min 2 letters).");
      ok = false;
    } else clearFieldError("member", "memberName");

    if (!form.relation) {
      setFieldError("member", "relation", "Please select relation.");
      ok = false;
    } else clearFieldError("member", "relation");

    if (!form.dob) {
      setFieldError("member", "dob", "Date of Birth is required.");
      ok = false;
    } else {
      const age = Number(calculateAge(form.dob));
      if (isNaN(age) || age < 0 || age > 120) {
        setFieldError("member", "dob", "Enter a valid DOB.");
        ok = false;
      } else clearFieldError("member", "dob");
    }

    if (!form.age) {
      setFieldError("member", "age", "Age auto-calculation failed. Enter valid DOB.");
      ok = false;
    } else clearFieldError("member", "age");

    return ok;
  };

  const validatePolicyForm = () => {
    let ok = true;

    if (policyForm.policyNumber && !policyNoPattern.test(policyForm.policyNumber.trim())) {
      setFieldError("policy", "policyNumber", "Invalid policy number (A-Z 0-9 - _ /).");
      ok = false;
    } else clearFieldError("policy", "policyNumber");

    if (!policyForm.policyHolderName.trim() || !namePattern.test(policyForm.policyHolderName.trim())) {
      setFieldError("policy", "policyHolderName", "Enter a valid policy holder name.");
      ok = false;
    } else clearFieldError("policy", "policyHolderName");

    if (!phonePattern.test(String(policyForm.contact).trim())) {
      setFieldError("policy", "contact", "Enter a valid 10-digit Indian mobile starting 6-9.");
      ok = false;
    } else clearFieldError("policy", "contact");

    if (!emailPattern.test(String(policyForm.email).trim())) {
      setFieldError("policy", "email", "Enter a valid email address.");
      ok = false;
    } else clearFieldError("policy", "email");

    if (!policyForm.company.trim()) {
      setFieldError("policy", "company", "Insurance company is required.");
      ok = false;
    } else clearFieldError("policy", "company");

    if (!policyForm.category) {
      setFieldError("policy", "category", "Select insurance category.");
      ok = false;
    } else clearFieldError("policy", "category");

    if (!policyForm.type.trim()) {
      setFieldError("policy", "type", "Enter policy type.");
      ok = false;
    } else clearFieldError("policy", "type");

    if (!policyForm.premium || !moneyPattern.test(String(policyForm.premium)) || Number(policyForm.premium) <= 0) {
      setFieldError("policy", "premium", "Enter a valid premium (e.g., 500 or 500.50).");
      ok = false;
    } else clearFieldError("policy", "premium");

    if (!policyForm.paymentMode) {
      setFieldError("policy", "paymentMode", "Select payment mode.");
      ok = false;
    } else clearFieldError("policy", "paymentMode");

    if (!policyForm.startDate) {
      setFieldError("policy", "startDate", "Start date is required.");
      ok = false;
    } else clearFieldError("policy", "startDate");

    if (!policyForm.maturityDate) {
      setFieldError("policy", "maturityDate", "Maturity date is required.");
      ok = false;
    } else clearFieldError("policy", "maturityDate");

    if (policyForm.startDate && policyForm.maturityDate) {
      const s = new Date(policyForm.startDate);
      const m = new Date(policyForm.maturityDate);
      if (m <= s) {
        setFieldError("policy", "maturityDate", "Maturity must be after start date.");
        ok = false;
      }
    }

    return ok;
  };

  const isMemberFormValid =
    form.policyNumber && form.primaryHolder && form.memberName && form.relation && form.dob && form.age;

  const handlePolicyChange = (e) => {
    const { name, value } = e.target;
    setPolicyForm(prev => ({ ...prev, [name]: value }));

    // live validation per field
    if (name === "policyNumber") {
      value ? (policyNoPattern.test(value) ? clearFieldError("policy", "policyNumber") : setFieldError("policy", "policyNumber", "Invalid policy number.")) : clearFieldError("policy", "policyNumber");
    }
    if (name === "policyHolderName") {
      namePattern.test(value.trim()) ? clearFieldError("policy", "policyHolderName") : setFieldError("policy", "policyHolderName", "Enter a valid name.");
    }
    if (name === "contact") {
      phonePattern.test(value.trim()) ? clearFieldError("policy", "contact") : setFieldError("policy", "contact", "Invalid mobile number.");
    }
    if (name === "email") {
      emailPattern.test(value.trim()) ? clearFieldError("policy", "email") : setFieldError("policy", "email", "Invalid email.");
    }
    if (name === "company") {
      value.trim() ? clearFieldError("policy", "company") : setFieldError("policy", "company", "Required.");
    }
    if (name === "category") {
      value ? clearFieldError("policy", "category") : setFieldError("policy", "category", "Required.");
    }
    if (name === "type") {
      value.trim() ? clearFieldError("policy", "type") : setFieldError("policy", "type", "Required.");
    }
    if (name === "premium") {
      moneyPattern.test(String(value)) && Number(value) > 0 ? clearFieldError("policy", "premium") : setFieldError("policy", "premium", "Invalid amount.");
    }
    if (name === "paymentMode") {
      value ? clearFieldError("policy", "paymentMode") : setFieldError("policy", "paymentMode", "Required.");
    }
    if (name === "startDate") {
      clearFieldError("policy", "startDate");
    }
    if (name === "maturityDate") {
      clearFieldError("policy", "maturityDate");
    }
  };

  const handleSearchCustomer = async () => {
    try {
      if (!validateSearchCustomer()) {
        showError("Please correct the highlighted fields.");
        return;
      }

      const policyNumber = searchCustomer.policyNumber.trim();
      const customerName = searchCustomer.name.trim();

      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/check-policy/${policyNumber}`);
      const data = await res.json();

      if (!data.exists) {
        showError("Policy not found. Please check the policy number.");
        return;
      }
      const primaryHolderName = data.policy?.customerName || customerName;
      const primaryPolicyNumber = data.policy?.policyNumber || policyNumber;

      if (primaryHolderName && primaryPolicyNumber) {
        const createRes = await fetch(`${import.meta.env.VITE_API_URL}/api/family/checkOrCreate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agentId: user?.id,
            primaryHolder: primaryHolderName,
            policyNumber: primaryPolicyNumber,
          }),
        });

        const createData = await createRes.json();
        if (createRes.ok) {
          setGroupId(createData.groupId);
          setFormSubmitted(true);

          const memberRes = await fetch(`${import.meta.env.VITE_API_URL}/api/family/group/${createData.groupId}`);
          const memberData = await memberRes.json();

          if (memberRes.ok) {
            setFamilyList(memberData.familyMembers || []);
          }
          setForm((prev) => ({
            ...prev,
            primaryHolder: primaryHolderName,
            policyNumber: primaryPolicyNumber,
          }));
        } else {
          showError("❌ " + (createData.msg || "Failed to fetch or create group"));
        }
      } else {
        showError("Please provide valid primary holder name and policy number.");
      }
    } catch (error) {
      console.error("Error searching customer:", error);
      showError("❌ Failed to search customer. Please try again.");
    }
  };

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (memberQuery.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/search-member/${memberQuery}`);
        const data = await res.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch (err) {
        console.error("Error fetching suggestions", err);
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };
    fetchSuggestions();
  }, [memberQuery]);

  // Fetching customer suggestions when user types
  useEffect(() => {
    const fetchCustomerSuggestions = async () => {
      if (customerQuery.length < 2) {
        setCustomerSuggestions([]);
        setCustomerShowSuggestions(false);
        return;
      }
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/search-customer/${user.id}/${customerQuery}`);
        const data = await res.json();
        setCustomerSuggestions(data);
        setCustomerShowSuggestions(data.length > 0);
      } catch (err) {
        console.error("Error fetching customer suggestions", err);
        setCustomerSuggestions([]);
        setCustomerShowSuggestions(false);
      }
    };
    if (user?.id) fetchCustomerSuggestions();
  }, [customerQuery, user?.id]);

  const handleMemberChange = (e) => {
    const { name, value } = e.target;
    if (name === "dob") {
      const age = calculateAge(value);
      setForm((prevForm) => ({ ...prevForm, [name]: value, age }));
      age ? clearFieldError("member", "dob") : setFieldError("member", "dob", "Enter a valid DOB.");
      if (age) clearFieldError("member", "age");
    } else {
      setForm((prevForm) => ({ ...prevForm, [name]: value }));
      if (name === "memberName") {
        setMemberQuery(value);
        namePattern.test(value.trim()) ? clearFieldError("member", "memberName") : setFieldError("member", "memberName", "Enter a valid member name.");
      }
      if (name === "relation") {
        value ? clearFieldError("member", "relation") : setFieldError("member", "relation", "Please select relation.");
      }
    }
  };

  const handleAddMember = () => {
    if (!validateMemberForm()) {
      showError("Please correct the highlighted member fields.");
      return;
    }
    showSuccess("✅ Member details valid. Now click 'Add Policy' to continue.");
  };

  const handleSavePolicy = async () => {
    // Member form must be valid first
    if (!validateMemberForm()) {
      showError("Please correct the member details first.");
      return;
    }
    // Policy validation
    if (!validatePolicyForm()) {
      showError("Please correct the highlighted policy fields.");
      return;
    }

    try {
      // Step 1: Save the member first
      const memberPayload = {
        agentId: user?.id,
        name: form.memberName,
        relation: form.relation,
        age: parseInt(form.age, 10),
        dob: form.dob || "",
      };

      const memberRes = await fetch(`${import.meta.env.VITE_API_URL}/api/family/add/${groupId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(memberPayload),
      });

      const memberData = await memberRes.json();
      if (!memberRes.ok) {
        console.error("❌ Member save failed:", memberData);
        showError(memberData.msg || "Failed to save member.");
        return;
      }

      // Step 2: Check if policy already exists (only if policy number is provided)
      if (policyForm.policyNumber) {
        const checkRes = await fetch(`${import.meta.env.VITE_API_URL}/api/check/${policyForm.policyNumber}`);
        const checkData = await checkRes.json();
        if (checkRes.ok && checkData.exists) {
          showSuccess("Member added successfully!");
          const updatedMembers = await fetch(`${import.meta.env.VITE_API_URL}/api/family/group/${groupId}`);
          const updatedData = await updatedMembers.json();
          if (updatedMembers.ok) setFamilyList(updatedData.familyMembers || []);
          handleResetForms();
          return;
        }
      }

      const policyPayload = {
        policyNumber: policyForm.policyNumber,
        customerName: policyForm.policyHolderName, // ✅ Fixed mapping
        customerPhone: policyForm.contact,
        customerEmail: policyForm.email || "",
        company: policyForm.company,
        insuranceType: policyForm.category,
        policyType: policyForm.type,
        agentId: user?.id,
        policyDetails: {
          premium: parseFloat(policyForm.premium),
          paymentMode: policyForm.paymentMode,
          startDate: policyForm.startDate,
          endDate: policyForm.maturityDate, // ✅ Fixed mapping
          branch: policyForm.branch || "",
        },
      };

      const policyRes = await fetch(`${import.meta.env.VITE_API_URL}/api/policies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(policyPayload),
      });

      const policyData = await policyRes.json();
      if (!policyRes.ok) {
        console.error("❌ Policy save failed:", policyData);
        showError(policyData.msg || "Failed to save policy.");
        return;
      }
      showSuccess("Member and Policy saved successfully!");

      // Refresh family members list
      try {
        const updatedMembers = await fetch(`${import.meta.env.VITE_API_URL}/api/family/group/${groupId}`);
        const updatedData = await updatedMembers.json();
        if (updatedMembers.ok) setFamilyList(updatedData.familyMembers || []);
      } catch (refreshError) {
        console.error("Failed to refresh family list:", refreshError);
      }
      handleResetForms();
    } catch (err) {
      console.error("Error while saving:", err);
      showError("Something went wrong. Please try again.");
    }
  };

  const handleDelete = async (index) => {
    try {
      const memberId = familyList[index]._id;
      if (!memberId) return;

      const deleteRes = await fetch(`${import.meta.env.VITE_API_URL}/api/family/delete/${groupId}/${memberId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (deleteRes.ok) {
        setFamilyList((prevList) => prevList.filter((_, i) => i !== index));
        showSuccess("✅ Member deleted successfully!");
      } else {
        showError("❌ Failed to delete member.");
      }
    } catch (error) {
      console.error("Error deleting member:", error);
      showError("❌ Error deleting member.");
    }
  };

  //customers
  const handleCustomerSelect = (suggestion) => {
    setSearchCustomer({
      name: suggestion.name,
      policyNumber: suggestion.policyNumber,
    });
    clearFieldError("search", "name");
    clearFieldError("search", "policyNumber");
    setCustomerShowSuggestions(false);
    setCustomerQuery("");
  };

  const handleResetForms = () => {
    setForm((prev) => ({
      ...prev,
      memberName: "",
      relation: "",
      dob: "",
      age: "",
    }));
    setPolicyForm({
      policyNumber: "",
      policyHolderName: "",
      contact: "",
      email: "",
      company: "",
      category: "",
      type: "",
      premium: "",
      paymentMode: "",
      startDate: "",
      maturityDate: "",
      branch: "",
    });
    setErrors({ search: {}, member: {}, policy: {} });
    setShowPolicyForm(false);
    setMemberQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Form styles
  const inputStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none'
  };

  const errorText = { color: "crimson", fontSize: 12, marginTop: 6 };

  const addPolicyButtonStyle = {
    backgroundColor: '#0094b7',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  };

  const saveButtonStyle = {
    marginTop: '15px',
    backgroundColor: '#3EAEB1',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  };

  const resetButtonStyle = {
    marginTop: '15px',
    backgroundColor: '#2b9cb7',
    color: 'white',
    border: 'none',
    padding: '10px 15px',
    borderRadius: '6px',
    fontWeight: '600',
    cursor: 'pointer'
  };

  const buttonPrimary = { backgroundColor: "#1e40af", color: "white", border: "none", padding: "10px 15px", borderRadius: "6px", fontWeight: "600", cursor: "pointer" };

  return (
    <div className="dashboard-layout d-flex">
      {/* ✅ Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      {sidebarOpen && <div className="overlay" onClick={toggleSidebar}></div>}

      <div className="main-content flex-grow-1" style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
        <div style={{ fontFamily: "Arial, sans-serif", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
          {/* Header */}
          <div style={{ backgroundColor: "#fff", padding: "20px", borderBottom: "1px solid #e0e0e0", marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: "1200px", margin: "0 auto" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <div>
                  <h3 style={{ margin: "0 0 5px 0", color: "#00728d", fontweight: "700", }}>Add Family Member</h3>
                  <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>Add family members to existing policy and manage their policies</p>
                </div>
              </div>
              <button onClick={() => navigate('/agent/family-history')} style={{ backgroundColor: "#36a9c6", color: "white", border: "none", padding: "10px 20px", borderRadius: "5px", cursor: "pointer", fontSize: "14px" }}>
                <FaEye style={{ marginRight: "5px" }} />
                View Existing Policy
              </button>
            </div>
          </div>

          {/* Search Section */}
          {!formSubmitted && (
            <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px" }}>
              <h3 style={{ color: "#006f8a", marginBottom: "20px", fontweight: "700", }}>Search Customer</h3>
              <div style={{ display: "flex", gap: "20px", backgroundColor: "white", padding: "20px", borderRadius: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div style={{ flex: 1, position: "relative" }}>
                  <label style={{ fontWeight: "bold" }}>Customer Name *</label>
                  <input
                    type="text"
                    placeholder="Enter customer name"
                    value={searchCustomer.name}
                    onChange={(e) => {
                      setSearchCustomer({ ...searchCustomer, name: e.target.value });
                      setCustomerQuery(e.target.value);
                      if (e.target.value.trim() && namePattern.test(e.target.value.trim())) {
                        clearFieldError("search", "name");
                      }
                    }}
                    style={inputStyle}
                  />
                  {errors.search.name && <div style={errorText}>{errors.search.name}</div>}
                  {/* Dropdown for customer suggestions */}
                  {customerShowSuggestions && customerSuggestions.length > 0 && (
                    <ul
                      style={{
                        listStyle: "none",
                        margin: 0,
                        padding: "5px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        background: "white",
                        position: "absolute",
                        zIndex: 2000,
                        width: "100%",
                        maxHeight: "150px",
                        overflowY: "auto",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      {customerSuggestions.map((suggestion) => (
                        <li
                          key={suggestion._id}
                          style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                          onClick={() => handleCustomerSelect(suggestion)}
                        >
                          {suggestion.name} - {suggestion.policyNumber}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <label style={{ fontWeight: "bold" }}>Policy Number *</label>
                  <input
                    type="text"
                    placeholder="Enter policy number"
                    value={searchCustomer.policyNumber}
                    onChange={(e) => {
                      setSearchCustomer({ ...searchCustomer, policyNumber: e.target.value });
                      if (e.target.value.trim() && policyNoPattern.test(e.target.value.trim())) {
                        clearFieldError("search", "policyNumber");
                      }
                    }}
                    style={inputStyle}
                  />
                  {errors.search.policyNumber && <div style={errorText}>{errors.search.policyNumber}</div>}
                </div>

                <div style={{ alignSelf: "flex-end" }}>
                  <button onClick={handleSearchCustomer} style={{ ...buttonPrimary, backgroundColor: "#006f8a", display: "flex", alignItems: "center", gap: "8px" }}>
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {formSubmitted && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
              {/* Left Section */}
              <div style={{ flex: "1" }}>
                <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                  {/* Group ID */}
                  <div style={{ backgroundColor: "#f8f9fa", padding: "0px 18px", borderRadius: "5px", marginBottom: "20px", border: "1px solid #e9ecef" }}>
                    <label style={{ fontWeight: "bold", color: "#495057", fontSize: "14px" }}>Group ID:</label>
                    <div style={{ fontSize: "16px", fontWeight: "bold", color: "#339db6", paddingBottom: "12px", }}>{groupId}</div>
                  </div>

                  {/* Member Form */}
                  {activeTab === "member" && (
                    <>
                      {/* Member Form Grid */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                          gap: "15px",
                          marginBottom: "20px",
                        }}
                      >
                        {/* Policy Number (Primary Holder Policy) */}
                        <div>
                          <label>Policy Number <span style={{ color: "red" }}>*</span></label>
                          <input
                            type="text"
                            name="policyNumber"
                            placeholder="Enter Policy Number"
                            value={form.policyNumber || ""}
                            onChange={handleMemberChange}
                            style={inputStyle}
                            readOnly
                          />
                          {errors.member.policyNumber && <div style={errorText}>{errors.member.policyNumber}</div>}
                        </div>

                        {/* Primary Holder */}
                        <div>
                          <label>Primary Holder Name <span style={{ color: "red" }}>*</span></label>
                          <input
                            type="text"
                            name="primaryHolder"
                            placeholder="Enter Primary Holder Name"
                            value={form.primaryHolder || ""}
                            onChange={handleMemberChange}
                            style={inputStyle}
                            readOnly
                          />
                          {errors.member.primaryHolder && <div style={errorText}>{errors.member.primaryHolder}</div>}
                        </div>

                        {/* Member Name with Suggestions */}
                        <div style={{ position: "relative" }}>
                          <label>Member Name <span style={{ color: "red" }}>*</span></label>
                          <div style={{ position: "relative" }}>
                            <input
                              type="text"
                              name="memberName"
                              placeholder="Enter Member Name"
                              value={form.memberName || ""}
                              onChange={(e) => {
                                handleMemberChange(e);
                                setMemberQuery(e.target.value);
                              }}
                              onFocus={() => {
                                if (memberQuery.length > 0) setShowSuggestions(true);
                              }}
                              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                              style={{ ...inputStyle, paddingRight: "35px" }}
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const res = await fetch(`${import.meta.env.VITE_API_URL}/api/family/search-member/all`);
                                  const data = await res.json();
                                  setSuggestions(data);
                                  setShowSuggestions(true);
                                  setMemberQuery("");
                                } catch (err) {
                                  console.error("Error fetching all members:", err);
                                  setMemberQuery("a");
                                  setShowSuggestions(true);
                                }
                              }}
                              style={{
                                position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)",
                                background: "none", border: "none", cursor: "pointer", fontSize: "16px", color: "#666"
                              }}
                            >
                              ▼
                            </button>
                          </div>
                          {errors.member.memberName && <div style={errorText}>{errors.member.memberName}</div>}

                          {/* Suggestions Dropdown */}
                          {showSuggestions && suggestions?.length > 0 && (
                            <ul style={{
                              listStyle: "none", margin: 0, padding: "5px", border: "1px solid #ccc",
                              borderRadius: "4px", background: "white", position: "absolute", zIndex: 2000,
                              width: "100%", maxHeight: "150px", overflowY: "auto", boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                            }}>
                              {suggestions.map((sug) => (
                                <li key={sug._id}
                                  style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #eee" }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={async () => {
                                    setForm((prev) => ({ ...prev, memberName: sug.name }));
                                    setSuggestions([]);
                                    setShowSuggestions(false);
                                    setMemberQuery("");
                                    clearFieldError("member", "memberName");

                                    try {
                                      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/by-id/${sug._id}`);
                                      const data = await res.json();
                                      if (res.ok && data) {
                                        setPolicyForm((prev) => ({
                                          ...prev,
                                          policyNumber: data.policyNumber || "",
                                          policyHolderName: data.customerName || sug.name,
                                          contact: data.customerPhone || "",
                                          email: data.customerEmail || "",
                                          company: data.company || "",
                                          category: data.insuranceType || "",
                                          type: data.policyType || "",
                                          premium: data.policyDetails?.premium || "",
                                          paymentMode: "",
                                          startDate: data.policyDetails?.startDate || "",
                                          maturityDate: data.policyDetails?.endDate || "",
                                        }));
                                        setIsPolicyFetched(true);
                                      } else {
                                        setPolicyForm((prev) => ({
                                          ...prev,
                                          policyHolderName: sug.name,
                                          contact: sug.contact || "",
                                        }));
                                      }
                                    } catch (err) {
                                      console.error("Error fetching policy data:", err);
                                    }
                                  }}
                                >
                                  <div style={{ fontWeight: "500" }}>{sug.name}</div>
                                  <div style={{ fontSize: "12px", color: "#666" }}>{sug.contact || "No Contact"}</div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>

                        {/* Relation */}
                        <div>
                          <label>Relation <span style={{ color: "red" }}>*</span></label>
                          <select
                            name="relation"
                            value={form.relation || ""}
                            onChange={handleMemberChange}
                            style={inputStyle}
                          >
                            <option value="">Select Relation</option>
                            <option>Wife</option>
                            <option>Son</option>
                            <option>Daughter</option>
                            <option>Mother</option>
                            <option>Father</option>
                          </select>
                          {errors.member.relation && <div style={errorText}>{errors.member.relation}</div>}
                        </div>

                        {/* DOB */}
                        <div>
                          <label>Date of Birth <span style={{ color: "red" }}>*</span></label>
                          <input
                            type="date"
                            name="dob"
                            value={form.dob || ""}
                            onChange={handleMemberChange}
                            style={inputStyle}
                          />
                          {errors.member.dob && <div style={errorText}>{errors.member.dob}</div>}
                        </div>

                        {/* Age */}
                        <div>
                          <label>Age (Auto-calculated) <span style={{ color: "red" }}>*</span></label>
                          <input
                            type="text"
                            name="age"
                            placeholder="Age will be calculated from DOB"
                            value={form.age || ""}
                            readOnly
                            style={{ ...inputStyle, backgroundColor: form.dob ? "#f8f9fa" : "white" }}
                          />
                          {errors.member.age && <div style={errorText}>{errors.member.age}</div>}
                        </div>
                      </div>

                      {/* Add Policy Button */}
                      {isMemberFormValid && !showPolicyForm && (
                        <button
                          type="button"
                          style={addPolicyButtonStyle}
                          onClick={() => {
                            if (!validateMemberForm()) {
                              showError("Please correct the highlighted member fields.");
                              return;
                            }
                            setShowPolicyForm(true);
                          }}
                        >
                          + Add Policy
                        </button>
                      )}

                      {/* Policy Form */}
                      {showPolicyForm && (
                        <div className="policy-form" style={{ marginTop: "20px" }}>
                          <div style={{
                            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px"
                          }}>
                            {/* Policy Number */}
                            <div>
                              <label>Policy Number</label>
                              <input
                                type="text"
                                name="policyNumber"
                                placeholder="Enter Policy Number (optional)"
                                value={policyForm.policyNumber || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.policyNumber && <div style={errorText}>{errors.policy.policyNumber}</div>}
                            </div>

                            {/* Policy Holder Name */}
                            <div>
                              <label>Policy Holder Name <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="text"
                                name="policyHolderName"
                                placeholder="Auto-fetched Member Name"
                                value={policyForm.policyHolderName || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.policyHolderName && <div style={errorText}>{errors.policy.policyHolderName}</div>}
                            </div>

                            {/* Contact */}
                            <div>
                              <label>Contact Number <span style={{ color: "red" }}>*</span> </label>
                              <input
                                type="text"
                                name="contact"
                                placeholder="Enter Contact Number"
                                value={policyForm.contact || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.contact && <div style={errorText}>{errors.policy.contact}</div>}
                            </div>

                            {/* Email */}
                            <div>
                              <label>Email <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="email"
                                name="email"
                                placeholder="Enter Email"
                                value={policyForm.email || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.email && <div style={errorText}>{errors.policy.email}</div>}
                            </div>

                            {/* Insurance Company */}
                            <div>
                              <label>Insurance Company <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="text"
                                name="company"
                                value={policyForm.company || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.company && <div style={errorText}>{errors.policy.company}</div>}
                            </div>

                            {/* Insurance Category */}
                            <div>
                              <label>Insurance Category <span style={{ color: "red" }}>*</span></label>
                              <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
                                <label>
                                  <input
                                    type="radio"
                                    name="category"
                                    value="Life Insurance"
                                    checked={policyForm.category === "Life Insurance"}
                                    onChange={handlePolicyChange}
                                  /> Life
                                </label>
                                <label>
                                  <input
                                    type="radio"
                                    name="category"
                                    value="General Insurance"
                                    checked={policyForm.category === "General Insurance"}
                                    onChange={handlePolicyChange}
                                  /> General
                                </label>
                              </div>
                              {errors.policy.category && <div style={errorText}>{errors.policy.category}</div>}
                            </div>

                            {/* Insurance Type */}
                            <div>
                              <label>Insurance Type <span style={{ color: "red" }}>*</span></label>
                              <input
                                name="type"
                                value={policyForm.type || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.type && <div style={errorText}>{errors.policy.type}</div>}
                            </div>

                            {/* Premium */}
                            <div>
                              <label>Premium Amount <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="number"
                                name="premium"
                                placeholder="Enter Premium Amount"
                                value={policyForm.premium || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.premium && <div style={errorText}>{errors.policy.premium}</div>}
                            </div>

                            {/* Payment Mode */}
                            <div>
                              <label>Payment Mode <span style={{ color: "red" }}>*</span></label>
                              <select
                                name="paymentMode"
                                value={policyForm.paymentMode || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              >
                                <option value="">Select Payment Mode</option>
                                <option>Monthly</option>
                                <option>Quarterly</option>
                                <option>Yearly</option>
                              </select>
                              {errors.policy.paymentMode && <div style={errorText}>{errors.policy.paymentMode}</div>}
                            </div>

                            {/* Start Date */}
                            <div>
                              <label>Policy Start Date <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="date"
                                name="startDate"
                                value={policyForm.startDate || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.startDate && <div style={errorText}>{errors.policy.startDate}</div>}
                            </div>

                            {/* Maturity Date */}
                            <div>
                              <label>Maturity Date <span style={{ color: "red" }}>*</span></label>
                              <input
                                type="date"
                                name="maturityDate"
                                value={policyForm.maturityDate || ""}
                                onChange={handlePolicyChange}
                                style={inputStyle}
                              />
                              {errors.policy.maturityDate && <div style={errorText}>{errors.policy.maturityDate}</div>}
                            </div>
                          </div>

                          {/* Save & Reset */}
                          <div style={{ marginTop: "15px", display: "flex", gap: "10px" }}>
                            <button type="button" style={saveButtonStyle} onClick={handleSavePolicy}>
                              Save Member & Policy
                            </button>
                            <button type="button" style={resetButtonStyle} onClick={handleResetForms}>
                              Reset
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                </div>
              </div>

              {/* Right Section: Existing Members */}
              <div style={{ flex: "1" }}>
                <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                  <h5 style={{ marginBottom: "15px", color: "#333" }}>Existing Family Members</h5>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Relation</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Age</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Status</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {familyList && familyList.length > 0 ? familyList.map((member, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '10px' }}>{member.name}</td>
                            <td style={{ padding: '10px' }}>{member.relation}</td>
                            <td style={{ padding: '10px' }}>{member.age}</td>
                            <td style={{ padding: '10px' }}>
                              <span style={{ backgroundColor: '#28a745', color: 'white', padding: '4px 8px', borderRadius: '12px', fontSize: '12px' }}>
                                {member.status || 'Active'}
                              </span>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <button
                                onClick={() => handleDelete(i)}
                                style={{ backgroundColor: '#fff6f7', color: 'dc3545', border: '1px solid', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                              >
                                <FaTrash />
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                              No family members added yet
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddFamilyMember;
